import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ToolCard from "../components/ToolCard";
import ToolFAQ from "../components/ToolFAQ";
import JsonLD, { buildBreadcrumb, buildFAQSchema } from "../components/JsonLD";
import { useLanguage } from "../context/LanguageContext";
import { TOOLS_LIST, SITE_NAME, SITE_URL } from "../translations";
import { useHreflang } from "../hooks/useHreflang";
import { buildLocalizedPath } from "../i18nConfig";

const meta = {
  en: {
    title: "Free PDF Tools Online — Merge, Compress, Convert PDFs",
    h1: "Free PDF Tools Online",
    description: "Browse 12 free PDF tools by FileNesto: merge, compress, split, convert, rotate, delete pages, protect PDFs, extract text and more — no signup.",
    intro: "Every PDF utility you need, in one place. Pick a tool below to merge PDF files, compress PDFs, convert, split, rotate, or protect documents in seconds. All tools are free, secure, and require no signup.",
    seoText: "FileNesto provides 12 essential PDF tools for individuals and professionals. Whether you need to merge contracts, compress presentations for email, extract tables for analysis, or protect sensitive documents with a password — all operations are available directly in your browser with no installation and no account required. Files are processed in memory and discarded immediately after download, ensuring complete privacy. Each tool supports PDF files up to 20MB and works on Windows, Mac, iOS, and Android devices.",
    faqs: [
      { q: "How many PDF tools does FileNesto offer?", a: "FileNesto offers 12 free PDF tools: merge, compress, split, PDF to Word, Word to PDF, PDF to Excel, PDF to Image, extract text, PDF preview, rotate pages, delete pages, and password protection." },
      { q: "Do I need to create an account to use PDF tools?", a: "No. All 12 PDF tools are available without any registration, signup, or email address." },
      { q: "What is the maximum PDF file size?", a: "Each PDF file can be up to 20MB. This is sufficient for the vast majority of documents including large reports and multi-page contracts." },
      { q: "Are my PDF files stored after processing?", a: "No. Files are processed in memory and immediately discarded once your download starts. FileNesto never stores, logs, or shares your documents." },
    ],
  },
  fr: {
    title: "Outils PDF gratuits en ligne — Fusionner, Compresser, Diviser",
    h1: "Outils PDF gratuits en ligne",
    description: "Découvrez tous les 12 outils PDF gratuits de FileNesto : fusionner, compresser, diviser, convertir, faire pivoter, supprimer des pages, protéger par mot de passe, extraire du texte, et plus.",
    intro: "Tous les utilitaires PDF dont vous avez besoin, en un seul endroit. Choisissez un outil ci-dessous pour fusionner, compresser, convertir ou transformer vos fichiers PDF en quelques secondes.",
    seoText: "FileNesto propose 12 outils PDF essentiels pour particuliers et professionnels. Que vous ayez besoin de fusionner des contrats, compresser des présentations pour l'email, extraire des tableaux pour analyse, ou protéger des documents sensibles par mot de passe — toutes les opérations sont disponibles directement dans votre navigateur, sans installation ni compte requis.",
    faqs: [
      { q: "Combien d'outils PDF propose FileNesto ?", a: "FileNesto propose 12 outils PDF gratuits : fusionner, compresser, diviser, PDF vers Word, Word vers PDF, PDF vers Excel, PDF vers Image, extraire le texte, aperçu PDF, faire pivoter, supprimer des pages et protection par mot de passe." },
      { q: "Faut-il créer un compte pour utiliser les outils PDF ?", a: "Non. Les 12 outils PDF sont disponibles sans aucune inscription, ni adresse email." },
      { q: "Quelle est la taille maximale de fichier PDF ?", a: "Chaque PDF peut aller jusqu'à 20 Mo, ce qui est suffisant pour la grande majorité des documents." },
      { q: "Mes fichiers PDF sont-ils stockés après traitement ?", a: "Non. Les fichiers sont traités en mémoire et immédiatement supprimés dès le début de votre téléchargement. FileNesto ne stocke, ne journalise ni ne partage jamais vos documents." },
    ],
  },
  es: {
    title: "Herramientas PDF gratuitas en línea — Fusionar, Comprimir, Dividir",
    h1: "Herramientas PDF gratuitas en línea",
    description: "Descubre las 12 herramientas PDF gratuitas de FileNesto: fusionar, comprimir, dividir, convertir, rotar, eliminar páginas, proteger con contraseña, extraer texto y más — sin registro.",
    intro: "Todas las utilidades PDF que necesitas en un solo lugar. Elige una herramienta para fusionar, comprimir, convertir o transformar tus archivos PDF en segundos.",
    seoText: "FileNesto ofrece 12 herramientas PDF esenciales para particulares y profesionales. Todas las operaciones están disponibles directamente en tu navegador, sin instalación ni cuenta requerida.",
    faqs: [
      { q: "¿Cuántas herramientas PDF ofrece FileNesto?", a: "FileNesto ofrece 12 herramientas PDF gratuitas: fusionar, comprimir, dividir, PDF a Word, Word a PDF, PDF a Excel, PDF a imagen, extraer texto, vista previa, rotar, eliminar páginas y proteger con contraseña." },
      { q: "¿Necesito crear una cuenta para usar las herramientas PDF?", a: "No. Las 12 herramientas PDF están disponibles sin ningún registro ni correo electrónico." },
      { q: "¿Cuál es el tamaño máximo de archivo PDF?", a: "Cada archivo PDF puede ser de hasta 20 MB, suficiente para la gran mayoría de documentos." },
      { q: "¿Mis archivos PDF se almacenan después del procesamiento?", a: "No. Los archivos se procesan en memoria y se eliminan inmediatamente una vez que comienza tu descarga." },
    ],
  },
  pt: {
    title: "Ferramentas PDF gratuitas online — Mesclar, Comprimir, Dividir",
    h1: "Ferramentas PDF gratuitas online",
    description: "Explore as 12 ferramentas PDF gratuitas do FileNesto: mesclar, comprimir, dividir, converter, girar, excluir páginas, proteger com senha, extrair texto e mais — sem cadastro.",
    intro: "Todos os utilitários PDF que você precisa em um só lugar. Escolha uma ferramenta para mesclar, comprimir, converter ou transformar seus arquivos PDF em segundos.",
    seoText: "O FileNesto oferece 12 ferramentas PDF essenciais para indivíduos e profissionais. Todas as operações estão disponíveis diretamente no seu navegador, sem instalação ou conta necessária.",
    faqs: [
      { q: "Quantas ferramentas PDF o FileNesto oferece?", a: "O FileNesto oferece 12 ferramentas PDF gratuitas: mesclar, comprimir, dividir, PDF para Word, Word para PDF, PDF para Excel, PDF para imagem, extrair texto, pré-visualização, girar, excluir páginas e proteção por senha." },
      { q: "Preciso criar uma conta para usar as ferramentas PDF?", a: "Não. As 12 ferramentas PDF estão disponíveis sem nenhum cadastro ou endereço de e-mail." },
      { q: "Qual é o tamanho máximo de arquivo PDF?", a: "Cada arquivo PDF pode ter até 20 MB, suficiente para a grande maioria dos documentos." },
      { q: "Meus arquivos PDF são armazenados após o processamento?", a: "Não. Os arquivos são processados na memória e imediatamente descartados assim que seu download começa." },
    ],
  },
  de: {
    title: "Kostenlose PDF-Tools online — PDF zusammenführen, komprimieren, teilen",
    h1: "Kostenlose PDF-Tools online",
    description: "Entdecke alle 12 kostenlosen PDF-Tools von FileNesto: zusammenführen, komprimieren, teilen, konvertieren, drehen, Seiten löschen, mit Passwort schützen, Text extrahieren und mehr.",
    intro: "Alle PDF-Dienstprogramme, die Sie benötigen, an einem Ort. Wählen Sie ein Tool, um Ihre PDF-Dateien in Sekunden zusammenzuführen, zu komprimieren, zu konvertieren oder zu transformieren.",
    seoText: "FileNesto bietet 12 essentielle PDF-Tools für Einzelpersonen und Profis. Alle Vorgänge sind direkt in Ihrem Browser verfügbar, ohne Installation und ohne Konto.",
    faqs: [
      { q: "Wie viele PDF-Tools bietet FileNesto an?", a: "FileNesto bietet 12 kostenlose PDF-Tools: zusammenführen, komprimieren, teilen, PDF zu Word, Word zu PDF, PDF zu Excel, PDF zu Bild, Text extrahieren, Vorschau, drehen, Seiten löschen und Passwortschutz." },
      { q: "Muss ich ein Konto erstellen, um die PDF-Tools zu nutzen?", a: "Nein. Alle 12 PDF-Tools sind ohne jede Registrierung oder E-Mail-Adresse verfügbar." },
      { q: "Was ist die maximale PDF-Dateigröße?", a: "Jede PDF-Datei kann bis zu 20 MB groß sein, ausreichend für die große Mehrheit der Dokumente." },
      { q: "Werden meine PDF-Dateien nach der Verarbeitung gespeichert?", a: "Nein. Dateien werden im Arbeitsspeicher verarbeitet und sofort gelöscht, sobald Ihr Download beginnt." },
    ],
  },
};

const TOOLS_LABEL = { en: "tools", fr: "outils", es: "herramientas", pt: "ferramentas", de: "Tools" };
const ALL_TOOLS_LABEL = { en: "All PDF Tools", fr: "Tous les outils PDF", es: "Todas las herramientas PDF", pt: "Todas as ferramentas PDF", de: "Alle PDF-Tools" };

export default function PdfTools() {
  const { t, lang } = useLanguage();
  const location = useLocation();
  const m = meta[lang] || meta.en;

  useHreflang("pdf-tools", "category");

  useEffect(() => {
    document.title = `${m.title} — ${SITE_NAME}`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", m.description);
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = `${SITE_URL}${location.pathname}`;
  }, [m, location.pathname]);

  return (
    <div className="min-h-screen bg-white page-fade-in" data-testid="page-pdf-tools">
      <JsonLD data={buildBreadcrumb(m.h1, location.pathname)} />
      {m.faqs?.length > 0 && <JsonLD data={buildFAQSchema(m.faqs)} />}
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="mb-6">
          <Link to={buildLocalizedPath("", lang) || "/"} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-blue-600 transition-colors">
            <ChevronLeft className="w-4 h-4" />{t.nav.home}
          </Link>
        </div>
        <header className="text-center mb-10">
          <span className="inline-block text-xs font-semibold uppercase tracking-wider text-green-700 bg-green-50 px-3 py-1 rounded-full mb-4">
            {t.categories.pdfBadge} · 12 {TOOLS_LABEL[lang] || TOOLS_LABEL.en}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-3 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {m.h1}
          </h1>
          <p className="text-slate-500 text-base max-w-2xl mx-auto">{m.intro}</p>
        </header>

        <h2 className="sr-only">{ALL_TOOLS_LABEL[lang] || ALL_TOOLS_LABEL.en}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="pdf-tools-grid">
          {TOOLS_LIST.map((tool) => (
            <ToolCard
              key={tool.slug}
              slug={tool.slug}
              icon={tool.icon}
              color={tool.color}
              title={t.tools[tool.key]?.title || tool.key}
              description={t.tools[tool.key]?.description || ""}
            />
          ))}
        </div>

        {m.seoText && (
          <div className="mt-12 p-6 bg-slate-50 rounded-2xl border border-slate-100">
            <p className="text-slate-600 text-sm leading-relaxed">{m.seoText}</p>
          </div>
        )}

        {m.faqs?.length > 0 && (
          <div className="mt-8 max-w-3xl mx-auto">
            <ToolFAQ faqs={m.faqs} />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
